import { AllWidgetTypes } from "components/widgets";
import { WidgetNames } from "constants/widgets";
import { mapValues } from "lodash";
import { useEffect, useState } from "react";
import { RememberSettingData } from "types/GlobalData";
import { Position } from "types/Positioning";
import { WidgetData, WidgetPosition, WidgetType } from "types/Widget";
import { loadData as wpLoadData, forceSaveData } from "util/wp-api";
import { v4 as uuidv4 } from "uuid";

export type WidgetDatas = {
  [id: string]: WidgetData;
};

export const LOCALSTORAGE_WIDGET_KEY = "WidgetData";

export interface newWidgetProps {
  typeId: string;
  zIndex: number;
  dashboardId: string;
  rememberSetting: RememberSettingData;
}

interface UseWidgetReturn {
  widgets: WidgetDatas;
  addWidget: (data: newWidgetProps) => string | any;
  removeWidget: (id: string) => void;
  updateWidgetPositions: (positions: { [id: string]: Partial<WidgetPosition> }) => void;
  updateWidgetState: (id: string, state: object, override?: boolean) => void;
  updateWidgetSetting: (id: string, setting: string, data: any) => void;
  duplicateWidget: (id: string, dashboardId: string) => string;
}

export const WIDGETS_WITHOUT_REMEMBER_SETTINGS = [WidgetNames.scheduler];

function getInitialSettings(typeId: string, rememberSetting: RememberSettingData) {
  const widget = AllWidgetTypes[typeId];
  const hasSomeSettings =
    rememberSetting &&
    rememberSetting.settings &&
    Object.keys(rememberSetting.settings).length >= 1;
  if (WIDGETS_WITHOUT_REMEMBER_SETTINGS.includes(typeId)) {
    return mapValues(widget.settings, (setting) => setting.defaultValue);
  }

  return rememberSetting && rememberSetting.isOn && hasSomeSettings
    ? rememberSetting.settings
    : mapValues(widget.settings, (setting) => setting.defaultValue);
}

export function useWidgets(
  loadWidgetData: (key: string) => Promise<any>,
  saveWidgetData: (key: string, data: any) => Promise<void>,
): UseWidgetReturn {
  const [loading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState<WidgetDatas>(
    localStorage.getItem(LOCALSTORAGE_WIDGET_KEY)
      ? JSON.parse(localStorage.getItem(LOCALSTORAGE_WIDGET_KEY)!)
      : {},
  );

  useEffect(() => {
    loadWidgetData(LOCALSTORAGE_WIDGET_KEY).then((data) => {
      setWidgets(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      //TODO we probably want to debounce saving the data, to reduce the amount of traffic
      // console.log("Saving Widget Data", widgets);
      saveWidgetData(LOCALSTORAGE_WIDGET_KEY, widgets);
    }
  }, [widgets, loading]);

  function updateWidgetPositions(positions: { [id: string]: Partial<WidgetPosition> }) {
    let newWidgets = { ...widgets };
    Object.keys(positions).forEach((key) => {
      newWidgets[key] = {
        ...widgets[key],
        position: {
          ...widgets[key].position,
          ...positions[key],
        },
      };
    });
    setWidgets(newWidgets);

    // Trigger WordPress API synchronization when widget positions change
    // This ensures that widget position changes are saved to WordPress
    try {
      forceSaveData(LOCALSTORAGE_WIDGET_KEY, newWidgets);
      console.log("Widget positions updated and saved to WordPress:", positions);

      // Also trigger a complete synchronization to ensure dashboard data is included
      setTimeout(async () => {
        try {
          // Load current dashboard data from localStorage to ensure we have the latest state
          const currentDashboardData = await wpLoadData("global");
          if (currentDashboardData) {
            // Force save dashboard data to ensure complete synchronization
            forceSaveData("DashboardData", currentDashboardData);
            console.log(
              "Complete dashboard synchronization triggered after widget position update",
            );
          }
        } catch (syncError) {
          console.error(
            "Failed to trigger complete dashboard synchronization after widget position update:",
            syncError,
          );
          // If WordPress API is not available (e.g., first login), ensure data is at least saved locally
          console.log("WordPress API not available, but widget position changes saved locally");
        }
      }, 100); // Small delay to ensure widget data is saved first
    } catch (error) {
      console.error("Failed to save widget position changes to WordPress:", error);
      // If WordPress API is not available (e.g., first login), ensure data is at least saved locally
      console.log("WordPress API not available, but widget position changes saved locally");
    }
  }

  function updateWidgetState(id: string, state: object, override?: boolean) {
    setWidgets({
      ...widgets,
      [id]: {
        ...widgets[id],
        state: override
          ? state
          : {
              ...widgets[id].state,
              ...state,
            },
      },
    });
  }

  function updateWidgetSetting(id: string, setting: string, data: any) {
    setWidgets({
      ...widgets,
      [id]: {
        ...widgets[id],
        settings: {
          ...widgets[id].settings,
          [setting]: data,
        },
      },
    });
  }

  function removeWidget(id: string) {
    setWidgets((prevWidgets) => {
      const { [id]: _, ...rest } = prevWidgets;
      return rest;
    });

    // Trigger complete synchronization after widget removal
    setTimeout(async () => {
      try {
        // Load current dashboard data from localStorage to ensure we have the latest state
        const currentDashboardData = await wpLoadData("global");
        if (currentDashboardData) {
          // Force save dashboard data to ensure complete synchronization
          forceSaveData("DashboardData", currentDashboardData);
          console.log("Complete dashboard synchronization triggered after widget removal");
        }
      } catch (syncError) {
        console.error(
          "Failed to trigger complete dashboard synchronization after widget removal:",
          syncError,
        );
        // If WordPress API is not available (e.g., first login), ensure data is at least saved locally
        console.log("WordPress API not available, but widget removal saved locally");
      }
    }, 100); // Small delay to ensure widget data is updated first
  }

  function initialWidgetPosition(widgetType: WidgetType<any>): Position {
    return {
      left: 50 - widgetType.defaultSize.widthPercentage / 2,
      top: 50 - widgetType.defaultSize.widthPercentage / widgetType.defaultSize.aspectRatio / 2,
    };
  }

  function addWidget({ typeId, zIndex, rememberSetting, dashboardId }: newWidgetProps) {
    const id = uuidv4();
    const widgetFullId = `${id}|${dashboardId}`;
    const type = AllWidgetTypes[typeId];

    const settings = getInitialSettings(typeId, rememberSetting);

    const newWidget = {
      id: widgetFullId,
      typeId: typeId,
      position: {
        ...type.defaultSize,
        //TODO add a function that generates a position based on what's free
        ...initialWidgetPosition(type),
        zIndex,
      },
      state: structuredClone(type.defaultState),
      settings: { ...settings },
    };

    const newWidgets = {
      ...widgets,
      [widgetFullId]: newWidget,
    };

    setWidgets(newWidgets);

    // Force immediate save to WordPress to ensure the new widget is saved
    // This will trigger the enhanced saveData function that reads all localStorage keys
    try {
      forceSaveData(LOCALSTORAGE_WIDGET_KEY, newWidgets);
      console.log("Widget created and immediately saved to WordPress:", widgetFullId);

      // Also trigger a complete synchronization to ensure dashboard data is included
      // This ensures that when widgets are added, the complete state is sent to WordPress
      setTimeout(async () => {
        try {
          // Load current dashboard data from localStorage to ensure we have the latest state
          const currentDashboardData = await wpLoadData("global");
          if (currentDashboardData) {
            // Force save dashboard data to ensure complete synchronization
            forceSaveData("DashboardData", currentDashboardData);
            console.log("Complete dashboard synchronization triggered after widget creation");
          }
        } catch (syncError) {
          console.error("Failed to trigger complete dashboard synchronization:", syncError);
          // If WordPress API is not available (e.g., first login), ensure data is at least saved locally
          console.log("WordPress API not available, but widget data saved locally");
        }
      }, 100); // Small delay to ensure widget data is saved first
    } catch (error) {
      console.error("Failed to immediately save widget to WordPress:", error);
      // If WordPress API is not available (e.g., first login), ensure data is at least saved locally
      console.log("WordPress API not available, but widget data saved locally");
    }

    return widgetFullId;
  }

  function duplicateWidget(id: string, dashboardId: string) {
    const widget = widgets[id];
    const newWidgetId = uuidv4();
    const newWidgetFullId = `${newWidgetId}|${dashboardId}`;
    const newWidget = {
      ...widget,
      id: newWidgetFullId,
    };
    setWidgets((prevWidgets) => {
      return {
        ...prevWidgets,
        [newWidgetFullId]: newWidget,
      };
    });

    // Trigger complete synchronization after widget duplication
    setTimeout(async () => {
      try {
        // Load current dashboard data from localStorage to ensure we have the latest state
        const currentDashboardData = await wpLoadData("global");
        if (currentDashboardData) {
          // Force save dashboard data to ensure complete synchronization
          forceSaveData("DashboardData", currentDashboardData);
          console.log("Complete dashboard synchronization triggered after widget duplication");
        }
      } catch (syncError) {
        console.error(
          "Failed to trigger complete dashboard synchronization after widget duplication:",
          syncError,
        );
        // If WordPress API is not available (e.g., first login), ensure data is at least saved locally
        console.log("WordPress API not available, but widget duplication saved locally");
      }
    }, 100); // Small delay to ensure widget data is updated first

    return newWidgetFullId;
  }

  return {
    widgets,
    updateWidgetPositions,
    updateWidgetState,
    removeWidget,
    addWidget,
    updateWidgetSetting,
    duplicateWidget,
  };
}
